/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";

import {
  CheckboxVisibility,
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  IDetailsRowProps,
  SelectionMode,
  Spinner,
  SpinnerSize,
} from "@fluentui/react";
import { Text } from "@fluentui/react/lib/Text";

import { errorMessageStyles } from "Components/AddComment/AddComment.styles";
import { detailListStyles, spinnerStyles } from "Components/Common/SharedListStyles.styles";
import { loadingErrorMessage } from "Constants/AwardCategoriesConstants";
import {
  getIsAwardCategoryUpdated,
  getSelectedAwardCategory,
  selectAwardCategory,
} from "Dataflows/AwardCategories/AwardCategoriesSlice";
import { IAwardCategoryData } from "Interfaces/AwardCategories/IAwardCategoriesResult";
import { useGetAwardCategoriesQuery } from "Services/AwardCategoriesApi";

import { IAwardCategoriesProps } from "./IAwardCategoriesProps";

const AwardCategories: React.FunctionComponent<IAwardCategoriesProps> = (
  props: IAwardCategoriesProps
) => {
  const { tenant } = props;
  const dispatch = useDispatch();
  const { data, error, isLoading, refetch } = useGetAwardCategoriesQuery(tenant);
  const isAwardCategoryUpdated = useSelector(getIsAwardCategoryUpdated);
  const selectedAwardCategory = useSelector(getSelectedAwardCategory);

  useEffect(() => {
    if (isAwardCategoryUpdated) {
      refetch(); // Refetch the data when the award category is updated
    }
  }, [isAwardCategoryUpdated]);

  const columns: IColumn[] = [
    {
      key: "technologyFocusAreaGroupId",
      name: "Award Category",
      ariaLabel: "Award Category Name",
      fieldName: "technologyFocusAreaGroupName",
      minWidth: 240,
      isResizable: true,
    },
    {
      key: "isActive",
      name: "Status",
      ariaLabel: "Status",
      fieldName: "isActive",
      minWidth: 120,
      onRender: (item: IAwardCategoryData): React.ReactElement => {
        if (item.isActive) {
          return <span>{"Active"}</span>;
        } else {
          return <span>{"Inactive"}</span>;
        }
      },
    },
  ];

  // Handle click event for selecting an award category
  const handleClick = (awardCategory: IAwardCategoryData): void => {
    const selectedCategoryID = selectedAwardCategory?.technologyFocusAreaGroupId;
    if (selectedCategoryID === awardCategory.technologyFocusAreaGroupId) {
      dispatch(selectAwardCategory({} as IAwardCategoryData));
    } else {
      dispatch(selectAwardCategory(awardCategory));
    }
  };

  const onRenderRow = (props?: IDetailsRowProps, defaultRender?: any): any => {
    const selectedAwardCategory = props!.item as any;
    return defaultRender!({
      ...props,
      onClick: () => handleClick(selectedAwardCategory),
    });
  };

  if (isLoading) {
    return <Spinner size={SpinnerSize.large} styles={spinnerStyles} />;
  }

  if (error) {
    return <Text styles={errorMessageStyles}>{loadingErrorMessage}</Text>;
  }

  return (
    <DetailsList
      items={data.awardCategoryData}
      columns={columns}
      selectionMode={SelectionMode.single}
      getKey={(item: IAwardCategoryData): string => item.technologyFocusAreaGroupId}
      setKey="set"
      ariaLabelForListHeader="Award Categories"
      ariaLabelForSelectionColumn="Toggle selection"
      checkButtonAriaLabel="select award category"
      checkboxVisibility={CheckboxVisibility.always}
      layoutMode={DetailsListLayoutMode.fixedColumns}
      styles={detailListStyles}
      isHeaderVisible={true}
      selectionPreservedOnEmptyClick={true}
      onRenderRow={onRenderRow}
      enterModalSelectionOnTouch={true}
    />
  );
};

export default AwardCategories;
